import { createFileRoute, Link } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import {
  Activity,
  ArrowLeft,
  CheckCircle2,
  AlertCircle,
  ExternalLink,
  MessageSquare,
  Plus,
  X,
} from "lucide-react";
import { api, ApiError } from "@/lib/api";
import { IssueDetailModal } from "@/features/tickets/IssueDetailModal";
import { ServicesHealthPanel } from "@/features/health/ServicesHealthPanel";

export const Route = createFileRoute("/_app/projects/$projectId")({
  component: ProjectDetailPage,
});

interface ProjectDetail {
  project: {
    id: string;
    name: string;
    slug: string;
    status: string;
    health: string;
    type: string;
    description: string | null;
    avatarR2Key: string | null;
    coverR2Key: string | null;
    startedAt: number | null;
    targetEndAt: number | null;
    updatedAt: number | null;
    posthogProjectUrl: string | null;
    posthogEnabled: boolean;
    hasPlane: boolean;
    categories: Array<{ id: string; name: string; slug: string; color: string | null }>;
    links: Array<{ id: string; kind: string; label: string; url: string }>;
  };
}

interface BoardIssue {
  id: string;
  identifier: string | null;
  sequenceId: number | null;
  title: string;
  state: string;
  stateGroup: string;
  priority: string;
  dueDate: string | null;
  updatedAt?: string;
}

interface Board {
  columns: Array<{ group: string; label: string; issues: BoardIssue[] }>;
  note?: string;
}

const PRIORITY_ORDER: Record<string, number> = {
  urgent: 1,
  high: 2,
  medium: 3,
  low: 4,
  none: 5,
};
const PRIORITY_LABEL: Record<string, string> = {
  urgent: "Urgente",
  high: "Alta",
  medium: "Media",
  low: "Baja",
  none: "Sin prioridad",
};
const PRIORITY_BADGE: Record<string, string> = {
  urgent: "badge-error",
  high: "badge-warning",
  medium: "badge-info",
  low: "badge-ghost",
  none: "badge-ghost",
};

function ProjectDetailPage() {
  const { projectId } = Route.useParams();
  const projectQ = useQuery<ProjectDetail>({
    queryKey: ["project", projectId],
    queryFn: () => api.get(`/projects/${projectId}`),
  });
  const boardQ = useQuery<Board>({
    queryKey: ["project", projectId, "board"],
    queryFn: () => api.get(`/projects/${projectId}/board`),
    enabled: !!projectQ.data?.project.hasPlane,
  });
  const [openIssueId, setOpenIssueId] = useState<string | null>(null);
  const [newTicketOpen, setNewTicketOpen] = useState(false);

  if (projectQ.isLoading) {
    return (
      <div className="grid place-items-center py-16">
        <span className="loading loading-spinner loading-lg text-primary" />
      </div>
    );
  }
  if (projectQ.isError || !projectQ.data) {
    return (
      <div className="card-elevated p-10 text-center text-sm text-base-content/55">
        No pudimos cargar este proyecto. Vuelve al listado.
      </div>
    );
  }
  const p = projectQ.data.project;

  return (
    <div className="space-y-6">
      <div className="flex justify-end gap-2 -mb-3">
        <Link to="/projects" className="btn btn-ghost btn-sm">
          <ArrowLeft className="size-4" /> Proyectos
        </Link>
        {p.hasPlane && (
          <button
            type="button"
            onClick={() => setNewTicketOpen(true)}
            className="btn btn-primary btn-sm"
          >
            <Plus className="size-4" /> Nuevo ticket
          </button>
        )}
      </div>

      <div className="card-elevated overflow-hidden">
        {p.coverR2Key ? (
          <div className="aspect-[4/1] bg-base-200 overflow-hidden">
            <img
              src={`/api/projects/${p.id}/cover?v=${p.updatedAt ?? ""}`}
              alt=""
              className="w-full h-full object-cover"
            />
          </div>
        ) : (
          <div className="aspect-[4/1] bg-gradient-to-br from-primary/20 to-secondary/20" />
        )}
        <div className="px-5 pb-5 -mt-12 relative flex items-end gap-4 flex-wrap">
          {p.avatarR2Key ? (
            <div className="size-20 rounded-xl overflow-hidden ring-4 ring-base-100 bg-base-100 shrink-0">
              <img
                src={`/api/projects/${p.id}/avatar?v=${p.updatedAt ?? ""}`}
                alt={p.name}
                className="w-full h-full object-cover"
              />
            </div>
          ) : (
            <div className="size-20 rounded-xl bg-primary/15 text-primary grid place-items-center ring-4 ring-base-100 shrink-0">
              <Plus className="size-8" />
            </div>
          )}
          <div className="min-w-0 flex-1 pt-2">
            <div className="text-xs font-semibold uppercase tracking-[0.18em] text-primary">
              Proyecto · {p.slug}
            </div>
            <h1 className="text-2xl md:text-3xl font-display font-semibold tracking-tight break-words">
              {p.name}
            </h1>
            {p.description && (
              <p className="text-sm text-base-content/70 mt-1 max-w-2xl leading-relaxed">
                {p.description}
              </p>
            )}
          </div>
        </div>
      </div>

      <UptimePanel projectId={projectId} />

      <ServicesHealthPanel projectSlug={p.slug} />

      {(p.categories.length > 0 || p.links.length > 0 || p.posthogProjectUrl) && (
        <div className="card-elevated p-4 flex flex-wrap items-center gap-2">
          {p.categories.map((c) => (
            <span
              key={c.id}
              className="badge badge-outline"
              style={c.color ? { borderColor: c.color, color: c.color } : undefined}
            >
              {c.name}
            </span>
          ))}
          {p.posthogEnabled && p.posthogProjectUrl && (
            <a
              href={p.posthogProjectUrl}
              target="_blank"
              rel="noreferrer"
              className="btn btn-ghost btn-xs gap-1"
            >
              PostHog <ExternalLink className="size-3" />
            </a>
          )}
          {p.links.map((l) => (
            <a
              key={l.id}
              href={l.url}
              target="_blank"
              rel="noreferrer"
              className="btn btn-ghost btn-xs gap-1"
            >
              {l.label} <ExternalLink className="size-3" />
            </a>
          ))}
        </div>
      )}

      {!p.hasPlane ? (
        <div className="card-elevated p-10 text-center text-sm text-base-content/55">
          Aún no hay tablero de tickets configurado para este proyecto.
        </div>
      ) : boardQ.isLoading ? (
        <div className="grid place-items-center py-16">
          <span className="loading loading-spinner text-primary" />
        </div>
      ) : (
        <KanbanBoard
          board={boardQ.data ?? { columns: [] }}
          onPickIssue={(id) => setOpenIssueId(id)}
        />
      )}

      {openIssueId && (
        <IssueDetailModal
          projectId={projectId}
          issueId={openIssueId}
          onClose={() => setOpenIssueId(null)}
        />
      )}
      {newTicketOpen && (
        <NewTicketModal projectId={projectId} onClose={() => setNewTicketOpen(false)} />
      )}
    </div>
  );
}

function KanbanBoard({
  board,
  onPickIssue,
}: {
  board: Board;
  onPickIssue: (id: string) => void;
}) {
  const cols = board.columns ?? [];
  return (
    <div className="overflow-x-auto -mx-4 px-4 md:mx-0 md:px-0">
      <div className="flex gap-3 min-w-min pb-2">
        {cols.map((col) => (
          <div
            key={col.group}
            className="bg-base-200/60 rounded-lg p-3 w-72 shrink-0 flex flex-col max-h-[75vh]"
          >
            <div className="flex items-center justify-between mb-2 px-1">
              <h3 className="text-xs font-semibold uppercase tracking-wider text-base-content/70">
                {col.label}
              </h3>
              <span className="text-xs text-base-content/55 tabular-nums">
                {col.issues.length}
              </span>
            </div>
            <ul className="space-y-2 overflow-y-auto flex-1">
              {col.issues
                .slice()
                .sort(
                  (a, b) =>
                    (PRIORITY_ORDER[a.priority] ?? 9) -
                    (PRIORITY_ORDER[b.priority] ?? 9),
                )
                .map((i) => (
                  <li key={i.id}>
                    <button
                      type="button"
                      onClick={() => onPickIssue(i.id)}
                      className="card-elevated p-3 w-full text-left hover:[&]:card-elevated-hover"
                    >
                      {i.identifier && (
                        <div className="text-[10px] font-mono text-base-content/55 mb-1">
                          {i.identifier}
                        </div>
                      )}
                      <div className="text-sm font-medium leading-snug line-clamp-3">
                        {i.title}
                      </div>
                      <div className="mt-1.5 flex items-center gap-2 flex-wrap">
                        {i.priority !== "none" && (
                          <span
                            className={`badge badge-xs ${PRIORITY_BADGE[i.priority] ?? "badge-ghost"}`}
                          >
                            {PRIORITY_LABEL[i.priority] ?? i.priority}
                          </span>
                        )}
                        {i.dueDate && (
                          <span className="text-[10px] text-base-content/55">
                            Vence {i.dueDate}
                          </span>
                        )}
                      </div>
                    </button>
                  </li>
                ))}
              {col.issues.length === 0 && (
                <li className="text-xs text-base-content/45 italic px-1 py-3 text-center">
                  Vacío
                </li>
              )}
            </ul>
          </div>
        ))}
      </div>
    </div>
  );
}

interface UptimeData {
  monitor: {
    lastStatus: "up" | "down" | "degraded" | null;
    lastCheckedAt: number | null;
    lastLatencyMs: number | null;
    uptimePct: number | null;
  } | null;
  checks: Array<{ checkedAt: number; status: "up" | "down" | "degraded"; latencyMs: number | null }>;
}

function UptimePanel({ projectId }: { projectId: string }) {
  const { data, isLoading } = useQuery<UptimeData>({
    queryKey: ["project", projectId, "uptime"],
    queryFn: () => api.get(`/projects/${projectId}/uptime`),
    refetchInterval: 60_000,
  });
  if (isLoading) return null;
  if (!data?.monitor) return null;
  const m = data.monitor;
  const statusLabel =
    m.lastStatus === "up"
      ? "Operativo"
      : m.lastStatus === "down"
        ? "Caído"
        : m.lastStatus === "degraded"
          ? "Degradado"
          : "Sin datos";
  const statusBadge =
    m.lastStatus === "up"
      ? "badge-success"
      : m.lastStatus === "down"
        ? "badge-error"
        : m.lastStatus === "degraded"
          ? "badge-warning"
          : "badge-ghost";
  const statusIcon =
    m.lastStatus === "up" ? (
      <CheckCircle2 className="size-4" />
    ) : (
      <AlertCircle className="size-4" />
    );

  return (
    <div className="card-elevated p-4 flex flex-col gap-3 sm:flex-row sm:items-center">
      <div className="flex items-center gap-3 flex-1">
        <div
          className={`size-10 rounded-lg grid place-items-center ${
            m.lastStatus === "up"
              ? "bg-success/15 text-success"
              : m.lastStatus === "down"
                ? "bg-error/15 text-error"
                : "bg-warning/15 text-warning"
          }`}
        >
          <Activity className="size-5" />
        </div>
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <span className={`badge ${statusBadge} gap-1`}>
              {statusIcon} {statusLabel}
            </span>
            {m.lastLatencyMs !== null && m.lastStatus === "up" && (
              <span className="text-xs text-base-content/55 tabular-nums">
                {m.lastLatencyMs} ms
              </span>
            )}
          </div>
          <div className="text-xs text-base-content/55">
            {m.uptimePct !== null ? `${m.uptimePct}% uptime · ` : ""}últimas 24h
            {m.lastCheckedAt && ` · revisado hace ${timeAgo(m.lastCheckedAt)}`}
          </div>
        </div>
      </div>
      {data.checks.length > 0 && (
        <div className="flex items-end gap-0.5 h-8 shrink-0">
          {data.checks.map((c, i) => (
            <div
              key={i}
              className={
                "w-1.5 rounded-sm " +
                (c.status === "up"
                  ? "bg-success/70 h-full"
                  : c.status === "degraded"
                    ? "bg-warning/70 h-3/4"
                    : "bg-error/70 h-1/2")
              }
              title={`${new Date(c.checkedAt * 1000).toLocaleTimeString("es-MX", {
                hour: "numeric",
                minute: "2-digit",
              })} — ${c.status}${c.latencyMs ? ` (${c.latencyMs}ms)` : ""}`}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function timeAgo(unixSeconds: number): string {
  const diff = Math.floor(Date.now() / 1000) - unixSeconds;
  if (diff < 60) return `${diff}s`;
  if (diff < 3600) return `${Math.floor(diff / 60)}min`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
  return `${Math.floor(diff / 86400)}d`;
}

function NewTicketModal({
  projectId,
  onClose,
}: {
  projectId: string;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [priority, setPriority] = useState<"urgent" | "high" | "medium" | "low" | "none">("medium");
  const [error, setError] = useState<string | null>(null);
  const create = useMutation({
    mutationFn: () =>
      api.post(`/projects/${projectId}/issues`, { title: title.trim(), description, priority }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["project", projectId, "board"] });
      onClose();
    },
    onError: (e) => setError(e instanceof ApiError ? JSON.stringify(e.payload) : String(e)),
  });
  return (
    <div
      className="modal modal-open"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (title.trim().length >= 3) create.mutate();
        }}
        className="modal-box max-w-lg"
      >
        <div className="flex items-baseline justify-between mb-3">
          <div>
            <div className="text-xs uppercase tracking-wider text-base-content/55">
              Nuevo ticket
            </div>
            <h3 className="font-display font-semibold text-lg">Reportar al equipo</h3>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="btn btn-ghost btn-sm btn-square"
            aria-label="Cerrar"
          >
            <X className="size-4" />
          </button>
        </div>

        <div className="space-y-3">
          <label className="form-control w-full">
            <span className="label-text text-xs font-semibold uppercase tracking-wider text-base-content/60 mb-1">
              Título
            </span>
            <input
              type="text"
              className="input input-bordered w-full"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              minLength={3}
              maxLength={255}
              required
              autoFocus
            />
          </label>
          <label className="form-control w-full">
            <span className="label-text text-xs font-semibold uppercase tracking-wider text-base-content/60 mb-1">
              Detalle
            </span>
            <textarea
              className="textarea textarea-bordered w-full"
              rows={5}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Pasos para reproducir, screenshots, contexto..."
            />
          </label>
          <label className="form-control w-full">
            <span className="label-text text-xs font-semibold uppercase tracking-wider text-base-content/60 mb-1">
              Prioridad
            </span>
            <select
              className="select select-bordered w-full"
              value={priority}
              onChange={(e) => setPriority(e.target.value as typeof priority)}
            >
              <option value="urgent">Urgente</option>
              <option value="high">Alta</option>
              <option value="medium">Media</option>
              <option value="low">Baja</option>
              <option value="none">Sin prioridad</option>
            </select>
          </label>
        </div>

        {error && (
          <div className="mt-3 text-sm text-error border border-error/30 bg-error/5 rounded-md p-2">
            {error}
          </div>
        )}

        <div className="modal-action">
          <button type="button" onClick={onClose} className="btn btn-ghost btn-sm">
            Cancelar
          </button>
          <button
            type="submit"
            disabled={create.isPending || title.trim().length < 3}
            className="btn btn-primary btn-sm"
          >
            {create.isPending && <span className="loading loading-spinner loading-xs" />}
            <MessageSquare className="size-4" /> Crear ticket
          </button>
        </div>
      </form>
    </div>
  );
}
